/**
 * `useComposeAttachmentUpload` — the post-send attachment transfer for the
 * member-message compose flow (feature 051, US1/US2).
 *
 * After `submitMessage` creates the real case and returns its id (`externalRef`),
 * the compose flow calls `uploadAll(caseId, attachments)` to transfer each
 * retained `File`'s real bytes to that case. It REUSES the member-conversation
 * upload path — `ports.memberCases.uploadAttachment(caseId, file, signal)` (the
 * `signal` bounds each upload with the per-file timeout below) — which runs the
 * server magic-byte sniff + size gate, ownership check, scan-gated storage via
 * the IS client, and the content-free audit. No new server route.
 *
 * Per-file OUTCOMES drive the honest result screen (US2): a partial failure
 * (case created, a file rejected/failed) is reported per file, never hidden
 * behind an unqualified success. If the member-cases port is absent (a wiring
 * bug) the files are honestly reported `unavailable` rather than silently
 * dropped.
 *
 * Single case, at most one upload pass per send: `uploadAll` uploads
 * sequentially and does not re-create the case (FR-008 / SC-003).
 */

import { useContext, useEffect, useRef, useState } from 'react';

import type { CasesReaderPort } from '../../member-cases/CasesReaderPort.js';
import { PortsContext } from '../../providers/PortsContext.js';
import type { ComposeAttachment } from './attachmentRef.js';

/**
 * The per-file transfer outcome — DERIVED from the member-cases upload port so
 * it tracks that union automatically (no hand-maintained mirror to drift).
 */
export type AttachmentUploadOutcome = Awaited<ReturnType<CasesReaderPort['uploadAttachment']>>;

export interface AttachmentUploadResult {
  readonly fileName: string;
  readonly outcome: AttachmentUploadOutcome;
}

export type AttachmentUploadPhase = 'idle' | 'uploading' | 'done';

/**
 * Per-file client-side upload timeout (051, US2 partial-failure backstop — see
 * FR-005 and the contract's §3 orchestration note). 60s was sized for the
 * former 10 MB compose ceiling (still the IS's observed runtime limit, O-020);
 * the client's advisory ceiling is now `MEMBER_ATTACHMENT_MAX_BYTES` (25 MB,
 * DL-027), and whether 60s must grow with it is tracked in OPEN-ISSUES O-020.
 * It bounds a stalled request so the member is not held on the "Saving
 * attachments…" view indefinitely — on
 * timeout the request is aborted and the file resolves to the honest
 * not-attached `unavailable` state (recoverable via the conversation).
 */
const UPLOAD_TIMEOUT_MS = 60_000;

export interface UseComposeAttachmentUploadResult {
  /**
   * Upload every attachment's real bytes to the created case, in order.
   * Resolves to the per-file results (also stored in `results`); sets `phase`
   * to `uploading` then `done`. A no-op returning `[]` for an empty list.
   */
  readonly uploadAll: (
    caseId: string,
    attachments: readonly ComposeAttachment[],
  ) => Promise<readonly AttachmentUploadResult[]>;
  readonly phase: AttachmentUploadPhase;
  readonly results: readonly AttachmentUploadResult[];
}

export function useComposeAttachmentUpload(): UseComposeAttachmentUploadResult {
  const ports = useContext(PortsContext);
  const [phase, setPhase] = useState<AttachmentUploadPhase>('idle');
  const [results, setResults] = useState<readonly AttachmentUploadResult[]>([]);

  // Unmount guard: an upload's network window can outlive the compose subtree
  // (the member closes the wizard mid-flight). The awaiting caller still gets
  // its results; only the STATE writes are gated.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const uploadAll = async (
    caseId: string,
    attachments: readonly ComposeAttachment[],
  ): Promise<readonly AttachmentUploadResult[]> => {
    if (attachments.length === 0) {
      return [];
    }
    if (mounted.current) {
      setPhase('uploading');
      setResults([]);
    }
    const port = ports?.memberCases;
    const collected: AttachmentUploadResult[] = [];
    // No upload path (wiring bug: port not wired) or no created case id to
    // target. Both are loop-invariant and unexpected — the compose surface only
    // mounts with the port wired, and a successful send always carries the case
    // id (externalRef invariant, is-case-routing-adapter) — so log the defect
    // ONCE per send here, before collapsing each file honestly to 'unavailable'
    // below (member still sees the honest not-attached result). No file
    // bytes/content in the log.
    const hasUploadPath = port !== undefined && caseId.length > 0;
    if (!hasUploadPath) {
      // biome-ignore lint/suspicious/noConsole: operator observability for a should-never-happen wiring bug; member still gets the honest 'unavailable'
      console.error('compose attachment upload: no upload path', {
        hasPort: port !== undefined,
        hasCaseId: caseId.length > 0,
      });
    }
    for (const attachment of attachments) {
      if (port === undefined || caseId.length === 0) {
        // Report the file honestly as not-attached (US2) rather than drop it
        // silently; the defect itself was logged once above.
        collected.push({ fileName: attachment.ref.fileName, outcome: 'unavailable' });
        continue;
      }
      // Bound each upload: a STALLED connection (not a clean failure) must not
      // trap the member on the pending view forever — after the timeout the
      // request is aborted and the file resolves to the honest not-attached
      // state (recoverable via the conversation). The IS/BFF have their own
      // server-side timeouts; this is the client-side backstop.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
      try {
        const outcome = await port.uploadAttachment(caseId, attachment.file, controller.signal);
        collected.push({ fileName: attachment.ref.fileName, outcome });
      } catch (error: unknown) {
        // The port contracts to RESOLVE its honest outcome, so a THROW is an
        // unexpected client-side defect — log it for operators (a throw is NOT a
        // transient 'unavailable'; without this the two are indistinguishable),
        // then collapse to a not-attached result rather than let it escape and
        // drop the file silently. No file bytes/content in the log.
        // biome-ignore lint/suspicious/noConsole: operator observability for an unexpected client-side defect; member still gets the honest 'unavailable'
        console.error('compose attachment upload threw unexpectedly', error);
        collected.push({ fileName: attachment.ref.fileName, outcome: 'unavailable' });
      } finally {
        clearTimeout(timer);
      }
    }
    if (mounted.current) {
      setResults(collected);
      setPhase('done');
    }
    return collected;
  };

  return { uploadAll, phase, results };
}
